import _regeneratorRuntime from "@babel/runtime/regenerator";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _asyncToGenerator from "@babel/runtime/helpers/esm/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["defaultMethod", "disableAutoMaskChar", "disabledMethods", "fieldProps", "messages", "onApplePayClick", "onChangeMethod", "onGooglePayClick", "onSubmit", "onSubmitSuccess", "submitting", "theme", "validate", "submitButtonProps"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useCallback, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import classNames from 'classnames/bind';
import { arrayOf, bool, func, node, object, oneOf, shape, string } from 'prop-types';
import { useMediaQuery } from "../hooks";
import { ApplePay, Card, CheckCircleLinear, GPay } from "../icons";
import noop from "../utils/noop";
import SelectField from '../Select';
import { SwitcherCanary } from '../SwitcherCanary';
import { Actions, Fields, Info } from './components';
import { methodType } from './constants';
import { formatCardNumberForMobile, getMethodsById, initializeMethod, transformCards } from './helpers';
import styles from './Payment.css';
var cx = classNames.bind(styles);

var getParametersByMethodType = function getParametersByMethodType(type, _ref) {
  var onSubmit = _ref.onSubmit,
      onGooglePayClick = _ref.onGooglePayClick,
      onApplePayClick = _ref.onApplePayClick;
  var buttonTheme = 'b2c';
  var submitFunc = onSubmit;

  if (type === methodType.APPLE_PAY) {
    buttonTheme = 'apple_pay_black';
    submitFunc = onApplePayClick;
  } else if (type === methodType.GOOGLE_PAY) {
    buttonTheme = 'google_pay_black';
    submitFunc = onGooglePayClick;
  }

  return {
    buttonTheme: buttonTheme,
    submitFunc: submitFunc
  };
};

var Payment = function Payment(_ref2) {
  var defaultMethod = _ref2.defaultMethod,
      disableAutoMaskChar = _ref2.disableAutoMaskChar,
      disabledMethods = _ref2.disabledMethods,
      fieldProps = _ref2.fieldProps,
      messages = _ref2.messages,
      onApplePayClick = _ref2.onApplePayClick,
      onChangeMethod = _ref2.onChangeMethod,
      onGooglePayClick = _ref2.onGooglePayClick,
      onSubmit = _ref2.onSubmit,
      onSubmitSuccess = _ref2.onSubmitSuccess,
      submittingProp = _ref2.submitting,
      theme = _ref2.theme,
      validate = _ref2.validate,
      submitButtonProps = _ref2.submitButtonProps,
      other = _objectWithoutProperties(_ref2, _excluded);

  var breakpoints = useMediaQuery('xs');
  var isMobile = breakpoints.xs;
  var cards = useMemo(function () {
    return other.cards.map(transformCards({
      disableAutoMaskChar: disableAutoMaskChar
    }));
  }, [disableAutoMaskChar, other.cards]);
  var methods = useMemo(function () {
    return [{
      icon: /*#__PURE__*/React.createElement(GPay, null),
      id: methodType.GOOGLE_PAY,
      label: 'Google Pay'
    }, {
      icon: /*#__PURE__*/React.createElement(ApplePay, null),
      id: methodType.APPLE_PAY,
      label: 'Apple Pay'
    }, {
      icon: /*#__PURE__*/React.createElement(Card, null),
      id: methodType.NEW_CARD,
      label: isMobile ? messages.newCard : messages.card,
      ids: [methodType.NEW_CARD].concat(_toConsumableArray(cards.map(function (option) {
        return option.id;
      })))
    }].filter(function (method) {
      return !disabledMethods.includes(method.id);
    });
  }, [isMobile, messages.newCard, messages.card, cards, disabledMethods]);
  var methodsById = useMemo(function () {
    return getMethodsById([].concat(_toConsumableArray(methods), _toConsumableArray(cards)));
  }, [methods, cards]);

  var _useState = useState(true),
      _useState2 = _slicedToArray(_useState, 2),
      isAgreementShort = _useState2[0],
      setIsAgreementShort = _useState2[1];

  var _useState3 = useState(initializeMethod([].concat(_toConsumableArray(methods), _toConsumableArray(cards)), defaultMethod, disabledMethods)),
      _useState4 = _slicedToArray(_useState3, 2),
      method = _useState4[0],
      setMethod = _useState4[1];

  var mobileOptionMapper = useCallback(function (option) {
    var _ref3 = methodsById[option.id] || {},
        id = _ref3.id,
        icon = _ref3.icon,
        label = _ref3.label,
        number = _ref3.number;

    return {
      value: id,
      label: number ? formatCardNumberForMobile(number) : label,
      left: icon,
      right: id === method && /*#__PURE__*/React.createElement("span", {
        className: cx('option-checked')
      }, /*#__PURE__*/React.createElement(CheckCircleLinear, {
        block: true,
        size: "md"
      }))
    };
  }, [methodsById, method]);

  var _getParametersByMetho = getParametersByMethodType(method, {
    onSubmit: onSubmit,
    onApplePayClick: onApplePayClick,
    onGooglePayClick: onGooglePayClick
  }),
      buttonTheme = _getParametersByMetho.buttonTheme,
      submitFunc = _getParametersByMetho.submitFunc;

  var isNewCard = method && method === methodType.NEW_CARD;
  var isGooglePay = method === methodType.GOOGLE_PAY;
  var isApplePay = method === methodType.APPLE_PAY;
  var isCard = method && !isGooglePay && !isApplePay;

  var handleChangeMore = function handleChangeMore() {
    setIsAgreementShort(!isAgreementShort);
  };

  var handleChangePaymentMethod = function handleChangePaymentMethod(id, formApi) {
    return function () {
      if (onChangeMethod) onChangeMethod({
        id: id
      });
      if (id !== method && formApi) formApi.reset();
      setMethod(id);
    };
  };

  var renderDesktopPaymentMethod = function renderDesktopPaymentMethod(formApi, isDisabled) {
    var normalizeOptions = methods.map(function (_ref4) {
      var label = _ref4.label,
          id = _ref4.id;
      return {
        label: label,
        value: id
      };
    });
    var currentValue = methods.reduce(function (_, curr) {
      if (curr.ids && curr.ids.includes(method)) {
        return curr.id;
      }

      return method;
    });
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: cx('options', {
        'disable-pointer-events': isDisabled
      })
    }, /*#__PURE__*/React.createElement(SwitcherCanary, {
      options: normalizeOptions,
      value: currentValue,
      onChange: function onChange(value) {
        return handleChangePaymentMethod(value, formApi)();
      }
    })));
  };

  var renderMobilePaymentMethod = function renderMobilePaymentMethod(formApi, isDisabled) {
    var handleChangeSelect = function handleChangeSelect(selectedOption) {
      handleChangePaymentMethod(selectedOption.value, formApi)();
    };

    var paymentOptions = methods.reduce(function (acc, option) {
      var newAcc = acc;

      if (option.id === methodType.NEW_CARD) {
        newAcc = newAcc.concat([].concat(_toConsumableArray(cards.map(function (card) {
          return mobileOptionMapper(card);
        })), [mobileOptionMapper(option)]));
      } else {
        newAcc.push(mobileOptionMapper(option));
      }

      return newAcc;
    }, []);
    var currentOption = paymentOptions.find(function (option) {
      return option.value === method;
    }) || null;
    return /*#__PURE__*/React.createElement(SelectField, {
      isDisabled: paymentOptions.length <= 1 || isDisabled,
      label: messages.paymentMethod,
      onChange: handleChangeSelect,
      options: paymentOptions,
      placeholder: messages.selectPaymentMethod,
      theme: theme,
      value: currentOption
    });
  };

  var _ref5 = methodsById[method] || {},
      _ref5$number = _ref5.number,
      number = _ref5$number === void 0 ? '' : _ref5$number,
      _ref5$holder = _ref5.holder,
      holder = _ref5$holder === void 0 ? '' : _ref5$holder,
      _ref5$expiration = _ref5.expiration,
      expiration = _ref5$expiration === void 0 ? '' : _ref5$expiration,
      _ref5$type = _ref5.type,
      cardType = _ref5$type === void 0 ? '' : _ref5$type;

  return /*#__PURE__*/React.createElement(Form, {
    initialValues: {
      agreement: false,
      cardExpiration: expiration,
      cardHolder: holder,
      cardNumber: number,
      cvc: ''
    },
    onSubmit: /*#__PURE__*/function () {
      var _ref6 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(values, form, cb) {
        var submit;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                _context.next = 2;
                return submitFunc(_objectSpread({
                  id: method
                }, values));

              case 2:
                submit = _context.sent;
                cb(submit);

                if (!(submit === undefined)) {
                  _context.next = 9;
                  break;
                }

                if (!onSubmitSuccess) {
                  _context.next = 8;
                  break;
                }

                onSubmitSuccess(form);
                return _context.abrupt("return");

              case 8:
                form.reset();

              case 9:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }));

      return function (_x, _x2, _x3) {
        return _ref6.apply(this, arguments);
      };
    }(),
    validate: validate,
    render: function render(_ref7) {
      var handleSubmit = _ref7.handleSubmit,
          submitting = _ref7.submitting,
          pristine = _ref7.pristine,
          form = _ref7.form,
          values = _ref7.values,
          hasValidationErrors = _ref7.hasValidationErrors;
      var isDisabled = submitting || submittingProp;
      return /*#__PURE__*/React.createElement("div", {
        className: cx('root')
      }, /*#__PURE__*/React.createElement("div", {
        className: cx('method')
      }, isMobile ? renderMobilePaymentMethod(form, isDisabled) : renderDesktopPaymentMethod(form, isDisabled)), /*#__PURE__*/React.createElement("form", {
        className: cx('form'),
        onSubmit: handleSubmit
      }, /*#__PURE__*/React.createElement(Fields, {
        cards: cards,
        card: isCard,
        disabled: isDisabled,
        newCard: isNewCard,
        themeRadio: theme,
        mobile: isMobile,
        method: method,
        form: form,
        number: number,
        onChangePaymentMethod: handleChangePaymentMethod,
        expiration: expiration,
        cardType: cardType,
        fieldProps: {
          cardNumber: fieldProps.cardNumber
        },
        values: {
          cardNumber: values.cardNumber
        },
        messages: {
          newCard: messages.newCard,
          cvc: messages.cvc,
          required: messages.required,
          requiredShort: messages.requiredShort,
          cardExpiration: messages.cardExpiration,
          cardNumber: messages.cardNumber,
          cardHolder: messages.cardHolder
        }
      }), /*#__PURE__*/React.createElement(Info, {
        mobile: isMobile,
        disabled: isDisabled,
        agreementShort: isAgreementShort,
        themeCheckbox: theme,
        onChangeMore: handleChangeMore,
        messages: {
          security: messages.security,
          required: messages.required,
          agreementShort: messages.agreementShort,
          agreement: messages.agreement,
          more: messages.more,
          hide: messages.hide
        }
      }), /*#__PURE__*/React.createElement(Actions, {
        mobile: isMobile,
        pristine: pristine,
        disabled: isDisabled,
        validationErrors: hasValidationErrors,
        buttonTheme: buttonTheme,
        messages: {
          buyInfo: messages.buyInfo,
          buyInfoMobile: messages.buyInfoMobile,
          amount: messages.amount,
          actionButton: messages.actionButton
        },
        submitButtonProps: submitButtonProps
      })));
    }
  });
};

var fieldPropShape = process.env.NODE_ENV !== "production" ? shape({
  iconRightProps: shape({
    isLoading: bool,
    onClick: func
  })
}) : {};
var fieldsPropShape = process.env.NODE_ENV !== "production" ? shape({
  cardNumber: fieldPropShape,
  cardExpiration: fieldPropShape,
  cvc: fieldPropShape,
  cardHolder: fieldPropShape
}) : {};
var messagePropShape = process.env.NODE_ENV !== "production" ? shape({
  actionButton: node,
  agreement: node,
  amount: node,
  buyInfo: node,
  buyInfoMobile: node,
  newCard: node,
  paymentMethod: node,
  security: node,
  selectPaymentMethod: node,
  cardNumber: node,
  cardHolder: node,
  cardExpiration: node,
  cvc: node,
  cardNumberValidation: node,
  cardHolderValidation: node,
  cardExpirationValidation: node,
  required: node,
  requiredShort: node
}) : {};
export var themes = ['b2c', 'b2b'];
Payment.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Список уже добавленных карт
   */
  cards: arrayOf(object),

  /**
   * Метод оплаты по умолчанию
   */
  defaultMethod: string,

  /**
   * Отключить маскирование символов
   */
  disableAutoMaskChar: bool,

  /**
   * Список отключенных методов оплаты
   */
  disabledMethods: arrayOf(string),

  /**
   * Дополнительные параметры для полей
   */
  fieldProps: fieldsPropShape,

  /**
   * Тексты для кнопок и полей
   */
  messages: messagePropShape,

  /**
   * Обратный вызов при нажатии на кнопку apple pay
   */
  onApplePayClick: func,

  /**
   * Обратный вызов при изменении методов оплаты
   */
  onChangeMethod: func,

  /**
   * Обратный вызов при нажатии на кнопку google pay
   */
  onGooglePayClick: func,

  /**
   * Обратный вызов при нажатии на кнопку оплаты
   */
  onSubmit: func,

  /**
   * Обратный вызов при успешной оплате
   */
  onSubmitSuccess: func,

  /**
   * Сделать форму недоступной для редактирования при отправки формы
   */
  submitting: bool,

  /**
   * Тема отображения
   */
  theme: oneOf(themes),

  /**
   * Валидация полей формы
   */
  validate: func,

  /**
   * Дополнительные параметры для кнопки submit
   */
  submitButtonProps: object
} : {};
Payment.defaultProps = {
  cards: [],
  defaultMethod: methodType.NEW_CARD,
  disabledMethods: [],
  onApplePayClick: noop,
  onGooglePayClick: noop,
  messages: {
    actionButton: 'Оплатить',
    agreement: 'Нажимая на кнопку, вы соглашаетесь с Основными условиями Электронной покупки, со всеми условиями и ограничениями для каждой выбранной услуги на вашем маршруте, а также с правилами и условиями перевозки пассажиров и багажа, а также предоставляете согласие на обработку персональных данных в соответствии с Положением конфиденциальности.',
    agreementShort: 'Нажимая на кнопку, вы соглашаетесь с Основными условиями электронной покупки, со всеми...',
    applePay: 'Apple Pay',
    buyInfoMobile: 'К оплате:',
    card: 'Банковская карта',
    hide: 'Скрыть',
    more: 'Подробнее',
    newCard: 'Новая карта',
    security: 'Все транзакции безопасны и зашифрованы',
    selectPaymentMethod: 'Выберите метод оплаты',
    cardNumber: 'Номер карты',
    cardHolder: 'Имя владельца',
    cardExpiration: 'MM / ГГ',
    cvc: 'CVC'
  },
  onSubmit: noop,
  theme: 'b2c',
  fieldProps: {}
};
export default Payment;